import * as React                from 'react';
import IntersectionContext       from './context';
import type { ObserverCallback } from './intersection.types';

const IntersectionProvider: React.FC<Omit<React.ProviderProps<{}>, 'value'>> = function(props) {
  const { children } = props;

  const callbacks = React.useRef(new WeakMap<Element, ObserverCallback>());
  const observer  = React.useRef<IntersectionObserver | null>(null);

  const observe   = React.useCallback((element: Element, callback: ObserverCallback) => {
    if (!callbacks.current.has(element)) {
      callbacks.current.set(element, callback);

      observer.current?.observe(element);
    }
  }, []);
  const unobserve = React.useCallback((element: Element) => {
    callbacks.current.delete(element);

    observer.current?.unobserve(element);
  }, []);

  const context = React.useMemo(() => {
    return {
      callbacks,
      observe,
      unobserve,
    };
  }, [observe, unobserve]);

  React.useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const callback = callbacks.current.get(entry.target);

          callback?.(entry.isIntersecting);
        });
      },
      {
        rootMargin: '200px',
        threshold:  0,
      },
    );

    return () => {
      observer.current?.disconnect();
      observer.current = null;
    };
  }, []);

  return (
    <IntersectionContext.Provider value={context}>
      {children}
    </IntersectionContext.Provider>
  );
};

export default IntersectionProvider;